import React, { useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Avatar,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  LinearProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Assignment as ProjectIcon,
  PushPin as PinIcon,
  Schedule as RecentIcon,
  TrendingUp as TrendingIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, Legend } from 'recharts';
import { projectService } from '../services/api';
import ProjectCountdown from '../components/ProjectCountdown';
import CalendarWidget from '../components/CalendarWidget';
import PrioritizationWidget from '../components/PrioritizationWidget';
import { useTranslation } from 'react-i18next';

const isElectron = typeof window !== 'undefined' && window.electronAPI?.isElectron === true;

function Dashboard() {
  const navigate = useNavigate();
  const { user } = useOutletContext();
  const { t } = useTranslation();
  const [stats, setStats] = useState({
    counts: {
      active_projects: 0,
      tasks_in_progress: 0,
      tasks_completed: 0,
      tasks_overdue: 0
    },
    pinned_projects: [],
    recent_projects: []
  });

  useEffect(() => {
    // Charger les statistiques
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await projectService.getDashboardStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    }
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  const getChartData = () => {
    const { tasks_in_progress, tasks_completed, tasks_overdue } = stats.counts;
    return [
      { name: t('dashboard.tasksInProgress'), value: tasks_in_progress },
      { name: t('dashboard.tasksCompleted'), value: tasks_completed },
      { name: t('dashboard.tasksOverdue'), value: tasks_overdue }
    ].filter(item => item.value > 0);
  };

  return (
    <Box>
      {/* En-tête de bienvenue */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 4 },
          mb: { xs: 2, md: 4 },
          borderRadius: 2,
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          color: 'white',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2
        }}
      >
        <Box>
          <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
            {t('dashboard.greeting', { name: user?.name?.split(' ')[0] || '' })}
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, fontSize: { xs: '1rem', md: '1.25rem' } }}>
            {t('dashboard.intro')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
          <Button
            variant="contained"
            sx={{
              bgcolor: 'white',
              color: 'primary.main',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
              boxShadow: '0 4px 14px 0 rgba(0,0,0,0.3)'
            }}
            size="medium"
            startIcon={<RecentIcon />}
            onClick={() => navigate('/meetings')}
          >
            {t('dashboard.meetingsButton')}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            size="medium"
            startIcon={<AddIcon />}
            onClick={() => navigate('/projects')}
            sx={{ boxShadow: '0 4px 14px 0 rgba(0,0,0,0.3)' }}
          >
            {t('dashboard.myProjectsButton')}
          </Button>
          {!isElectron && (
            <Button
              variant="contained"
              size="medium"
              startIcon={<DownloadIcon />}
              href="/ERA Project Setup 1.0.0.exe"
              download
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.4)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                boxShadow: '0 4px 14px 0 rgba(0,0,0,0.3)',
                display: { xs: 'none', sm: 'inline-flex' }
              }}
            >
              {t('dashboard.downloadDesktop')}
            </Button>
          )}
        </Box>
      </Paper>

      {/* Widget IA de Priorisation */}
      <PrioritizationWidget />

      <Grid container spacing={3}>
        {/* Colonne Gauche: Stats et Graphiques */}
        <Grid item xs={12} md={8}>
          
          {/* Cartes de statistiques */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.light', mr: 2 }}>
                      <ProjectIcon />
                    </Avatar>
                    <Typography variant="h4" fontWeight="bold">
                      {stats.counts.active_projects}
                    </Typography>
                  </Box>
                  <Typography color="text.secondary" variant="body2">
                    {t('dashboard.activeProjects')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'info.light', mr: 2 }}>
                      <TrendingIcon />
                    </Avatar>
                    <Typography variant="h4" fontWeight="bold">
                      {stats.counts.tasks_in_progress}
                    </Typography>
                  </Box>
                  <Typography color="text.secondary" variant="body2">
                    {t('dashboard.tasksInProgress')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'success.light', mr: 2 }}>
                      <ProjectIcon />
                    </Avatar>
                    <Typography variant="h4" fontWeight="bold">
                      {stats.counts.tasks_completed}
                    </Typography>
                  </Box>
                  <Typography color="text.secondary" variant="body2">
                    {t('dashboard.tasksCompleted')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'error.light', mr: 2 }}>
                      <ProjectIcon />
                    </Avatar>
                    <Typography variant="h4" fontWeight="bold" color="error">
                      {stats.counts.tasks_overdue}
                    </Typography>
                  </Box>
                  <Typography color="text.secondary" variant="body2">
                    {t('dashboard.tasksOverdue')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Graphique et Projets Récents */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  {t('dashboard.overviewTitle')}
                </Typography>
                <Box sx={{ height: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  {stats.counts.active_projects === 0 ? (
                     <Typography color="text.secondary">{t('dashboard.noData')}</Typography>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={getChartData()}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={80}
                          fill="#8884d8"
                          paddingAngle={5}
                          dataKey="value"
                        >
                          {getChartData().map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <RechartsTooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ borderRadius: 2, height: '100%', overflow: 'hidden' }}>
                <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderBottom: '1px solid #eee' }}>
                  <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                    <RecentIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    {t('dashboard.recentProjectsTitle')}
                  </Typography>
                </Box>
                <List sx={{ p: 0 }}>
                  {stats.recent_projects.length > 0 ? (
                    stats.recent_projects.map((project, index) => (
                      <React.Fragment key={project.id}>
                        {index > 0 && <Divider />}
                        <ListItem button onClick={() => navigate(`/projects`)}>
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: project.color || 'primary.main', fontSize: '1rem' }}>
                              {project.title.charAt(0).toUpperCase()}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography variant="subtitle1" fontWeight="medium">
                                {project.title}
                              </Typography>
                            }
                            secondaryTypographyProps={{ component: 'div' }}
                            secondary={
                              <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                <Chip 
                                  label={project.status} 
                                  size="small" 
                                  color={project.status === 'active' ? 'success' : 'default'}
                                  sx={{ height: 20, fontSize: '0.7rem', mr: 1 }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  {t('dashboard.recentProjectsProgress', { completed: project.completed_tasks, total: project.total_tasks })}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      </React.Fragment>
                    ))
                  ) : (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                      <Typography color="text.secondary">{t('dashboard.noRecentProjects')}</Typography>
                    </Box>
                  )}
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Grid>

        {/* Colonne Droite: Épinglés et Activité */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ borderRadius: 2, mb: 3 }}>
            <Box sx={{ p: 2, bgcolor: '#fff8e1', borderBottom: '1px solid #ffe082', borderTopLeftRadius: 8, borderTopRightRadius: 8 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', color: '#f57c00' }}>
                <PinIcon sx={{ mr: 1 }} />
                {t('dashboard.pinnedTitle')}
              </Typography>
            </Box>
            <CardContent>
              {stats.pinned_projects.length > 0 ? (
                stats.pinned_projects.map((project) => (
                  <Card 
                    key={project.id} 
                    variant="outlined" 
                    sx={{ mb: 2, cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }}
                    onClick={() => navigate(`/projects`)}
                  >
                    <CardContent sx={{ pb: '16px !important' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {project.title}
                        </Typography>
                        {project.end_date && (
                          <ProjectCountdown 
                            endDate={project.end_date} 
                            startDate={project.start_date}
                            compact
                          />
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={project.total_tasks > 0 ? (project.completed_tasks / project.total_tasks) * 100 : 0} 
                          sx={{ flexGrow: 1, height: 6, borderRadius: 3, mr: 1 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {project.total_tasks > 0 ? Math.round((project.completed_tasks / project.total_tasks) * 100) : 0}%
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('dashboard.noPinnedTitle')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('dashboard.noPinnedSubtitle')}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Paper>

          <Paper elevation={2} sx={{ borderRadius: 2, p: 3, background: 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)' }}>
             <Typography variant="h6" gutterBottom fontWeight="bold">
                {t('dashboard.tipTitle')}
             </Typography>
             <Typography variant="body2" color="text.secondary">
                {t('dashboard.tipText')}
             </Typography>
          </Paper>
        </Grid>

        {/* Agenda / Calendrier */}
        <Grid item xs={12} sx={{ mb: 4 }}>
          <CalendarWidget />
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard;
